/* FI offer cleanup only. Disabled unless the server-rendered config says enabled:true.
 * Config: #vireva-fi-offer-cart-config (application/json):
 * { enabled:false, hasTaggedOffer:false, country:"FI", currency:"EUR", rootUrl:"/",
 *   insoleVariantIds:[...], giftVariantIds:[...], errorMessage:"<translated>" }
 * Keeps at most ONE eligible tagged gift pack per whole cart (first cart order).
 * Removes all tagged gifts when the cart moves outside FI/EUR.
 * Never adds products, grants discounts, or changes paid StepDry quantities.
 */
(() => {
  'use strict';
  if (window.VirevaFiOfferCartGuard) return;
  const node = document.getElementById('vireva-fi-offer-cart-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch (_) { return; }
  if (config.enabled !== true) return;
  const offerMarket = config.country === 'FI' && config.currency === 'EUR';

  const ids = values => new Set((Array.isArray(values) ? values : [])
    .map(Number).filter(id => Number.isSafeInteger(id) && id > 0));
  const insoleIds = ids(config.insoleVariantIds);
  const giftIds = ids(config.giftVariantIds);
  const errorMessage = typeof config.errorMessage === 'string' ? config.errorMessage :
    (typeof config.error === 'string' ? config.error : '');
  let root;
  try {
    root = new URL(config.rootUrl || '/', window.location.origin);
    if (root.origin !== window.location.origin) root = null;
    else { root.search = ''; root.hash = ''; root.pathname = root.pathname.replace(/\/?$/, '/'); }
  } catch (_) { root = null; }
  const validConfig = root && /^[A-Z]{2}$/.test(config.country || '') &&
    /^[A-Z]{3}$/.test(config.currency || '') &&
    insoleIds.size === 8 && giftIds.size === 6 && errorMessage &&
    !Array.from(giftIds).some(id => insoleIds.has(id));
  const endpoint = path => new URL(path, root).href;
  let requested = false;
  let worker = null;
  let checkoutPending = false;
  let bypassCheckout = false;
  let needsRefresh = false;
  let requiresAudit = config.hasTaggedOffer === true;
  let offerRevision = 0;
  let subscription = null;
  let subscriptionAttempts = 0;
  const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));

  function hasOfferTags(cart) {
    return Boolean(cart && Array.isArray(cart.items) && cart.items.some(item => {
      const properties = item.properties || {};
      return properties._vireva_fi_gift === '1' ||
        (typeof properties._vireva_fi_group === 'string' && properties._vireva_fi_group.trim());
    }));
  }

  function markOfferPending() {
    requiresAudit = true;
    offerRevision += 1;
    // The adapter calls this before sending its gift payload. Do not fetch yet:
    // the normal post-ATC event will audit the committed cart.
  }

  async function readCart() {
    const revision = offerRevision;
    const cart = await jsonRequest(endpoint('cart.js'));
    if (!cart || !Array.isArray(cart.items) || cart.currency !== config.currency) {
      throw new Error('Invalid cart for FI offer cleanup');
    }
    if (hasOfferTags(cart)) requiresAudit = true;
    // An older in-flight response must not erase a newer adapter's pending mark.
    else if (revision === offerRevision) requiresAudit = false;
    return cart;
  }

  function showError() {
    let error = document.getElementById('vireva-fi-offer-cart-error');
    if (!error) {
      error = document.createElement('p');
      error.id = 'vireva-fi-offer-cart-error';
      error.setAttribute('role', 'alert');
    }
    // Reattach after section rendering. Keep the message beside the active cart.
    const drawer = document.querySelector('cart-drawer.active .drawer__footer');
    const host = drawer || document.getElementById('main-cart-footer') ||
      document.querySelector('cart-drawer .drawer__footer') || document.body;
    if (!host.contains(error)) host.appendChild(error);
    error.textContent = errorMessage;
    error.hidden = false;
  }

  function clearError() {
    const error = document.getElementById('vireva-fi-offer-cart-error');
    if (error) error.hidden = true;
  }

  async function jsonRequest(url, options) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store',
        ...options,
        headers: { Accept: 'application/json', ...(options && options.headers) },
        signal: controller.signal
      });
      if (!response.ok) throw new Error('FI offer cart request failed: HTTP ' + response.status);
      const data = await response.json();
      if (data && data.errors) throw new Error('FI offer cart response error');
      return data;
    } finally { window.clearTimeout(timer); }
  }

  async function waitForNativeCart() {
    const started = Date.now();
    let idleSince = null;
    while (Date.now() - started < 5000) {
      const busy = document.querySelector(
        '#main-cart-items.cart__items--disabled, #CartDrawer-CartItems.cart__items--disabled, ' +
        '.main-product-form .product-form__submit.loading, .main-product-atc.loading'
      );
      if (busy) idleSince = null;
      else if (idleSince === null) idleSince = Date.now();
      else if (Date.now() - idleSince >= 150) return;
      await delay(50);
    }
    throw new Error('Native cart remained busy');
  }

  function giftUpdates(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.currency !== config.currency) {
      throw new Error('Invalid cart for FI offer cleanup');
    }
    const groupQuantity = new Map();
    for (const item of cart.items) {
      const properties = item.properties || {};
      const group = properties._vireva_fi_group;
      if (typeof group !== 'string' || !group.trim() || properties._vireva_fi_gift === '1' ||
          !insoleIds.has(Number(item.variant_id))) continue;
      const quantity = Number(item.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid parent quantity');
      groupQuantity.set(group, (groupQuantity.get(group) || 0) + quantity);
    }
    const updates = {};
    let keptGift = false;
    for (const item of cart.items) {
      const properties = item.properties || {};
      const group = properties._vireva_fi_group;
      if (properties._vireva_fi_gift !== '1' || !giftIds.has(Number(item.variant_id))) continue;
      if (typeof item.key !== 'string' || !item.key) throw new Error('Missing gift line key');
      const quantity = Number(item.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Invalid gift quantity');
      if (!offerMarket || typeof group !== 'string' || !group.trim() ||
          (groupQuantity.get(group) || 0) < 4 || keptGift) {
        updates[item.key] = 0;
      } else {
        // Deterministic: preserve the first eligible tagged line in cart order.
        keptGift = true;
        if (quantity !== 1) updates[item.key] = 1;
      }
    }
    return updates;
  }

  function sectionPlan() {
    const drawer = document.querySelector('cart-drawer');
    const main = document.querySelector('cart-items');
    const drawerSections = drawer && typeof drawer.getSectionsToRender === 'function' &&
      typeof drawer.renderContents === 'function' ? drawer.getSectionsToRender() : [];
    const mainSections = main && typeof main.getSectionsToRender === 'function' ? main.getSectionsToRender() : [];
    const sections = [...new Set([...drawerSections, ...mainSections].map(s => s.section || s.id).filter(Boolean))];
    if (sections.length > 5) throw new Error('Unexpected cart section count');
    return { drawer, main, drawerSections, mainSections, sections };
  }

  async function refreshCart(cart) {
    const plan = sectionPlan();
    if (!plan.sections.length) return;
    // Request fresh HTML after cleanup instead of rendering a stale ATC response.
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', plan.sections.join(','));
    const sections = await jsonRequest(url.href);
    for (const key of plan.sections) {
      if (typeof sections[key] !== 'string') throw new Error('Cart section rendering failed');
    }
    const parser = new DOMParser();
    // Prepare all main-cart replacements before changing any DOM.
    const replacements = plan.mainSections.map(section => {
      const container = document.getElementById(section.id);
      if (!container) return null;
      const parsed = parser.parseFromString(sections[section.section || section.id], 'text/html');
      const source = parsed.querySelector(section.selector || '.shopify-section');
      const target = container.querySelector(section.selector || '.shopify-section') || container;
      if (!source) throw new Error('Missing main cart section content');
      return { target, html: source.innerHTML };
    }).filter(Boolean);
    if (plan.drawerSections.length && plan.drawer.isConnected) {
      // Shrine's second argument suppresses opening a closed drawer.
      plan.drawer.renderContents({ ...cart, sections }, true);
      plan.drawer.classList.toggle('is-empty', cart.item_count === 0);
    }
    replacements.forEach(({ target, html }) => { if (target.isConnected) target.innerHTML = html; });
    if (plan.main && plan.main.isConnected) plan.main.classList.toggle('is-empty', cart.item_count === 0);
    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', cart.item_count === 0);
  }

  async function auditOnce() {
    if (!validConfig) throw new Error('Invalid FI cart guard configuration');
    await waitForNativeCart();
    let cart = await readCart();
    let updates = giftUpdates(cart);
    if (Object.keys(updates).length) {
      // Re-read immediately before mutation: only fresh line keys qualify.
      await waitForNativeCart();
      cart = await readCart();
      updates = giftUpdates(cart);
      if (Object.keys(updates).length) {
        needsRefresh = true;
        await jsonRequest(endpoint('cart/update.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        cart = await readCart();
        if (Object.keys(giftUpdates(cart)).length) throw new Error('Gift cleanup did not settle');
      }
    }
    if (needsRefresh) {
      await refreshCart(cart);
      // Public Shrine rendering can trigger native gift reconciliation. Give
      // those callbacks their turn and audit once more before checkout resumes.
      needsRefresh = false;
      requested = true;
      await delay(200);
    }
    return cart;
  }

  function audit() {
    requested = true;
    if (!worker) {
      worker = (async () => {
        let cart;
        let passes = 0;
        while (requested) {
          if (++passes > 8) throw new Error('Cart did not stabilize');
          requested = false;
          cart = await auditOnce();
        }
        clearError();
        return cart;
      })().catch(error => {
        if (requiresAudit) showError();
        throw error;
      }).finally(() => { worker = null; });
    }
    return worker;
  }

  function auditAfterChange(event) {
    const cartData = event && (event.cartData || event.detail?.cartData);
    if (hasOfferTags(cartData)) markOfferPending();
    audit().catch(() => {});
  }

  function checkoutLink(element) {
    if (!element || !element.matches('a[href]')) return false;
    try {
      const url = new URL(element.href, window.location.origin);
      return url.origin === window.location.origin && /(^|\/)checkout(?:\/|$)/.test(url.pathname);
    } catch (_) { return false; }
  }

  function intercept(event, action) {
    if (bypassCheckout || (!requiresAudit && !worker)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    const resume = () => {
      bypassCheckout = true;
      try { action(); } finally { bypassCheckout = false; }
    };
    audit().then(resume, () => {
      // A failed background read must not lock an unrelated Finnish cart.
      // Failed reads never clear a previously established offer requirement.
      if (!requiresAudit) resume();
      else showError();
    }).catch(() => { showError(); }).finally(() => { checkoutPending = false; });
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target.closest('a[href], button, input[type="submit"]') : null;
    if (!target || !(checkoutLink(target) || target.getAttribute('name') === 'checkout')) return;
    // Native checkout buttons already dispatch submit after their own click/TNC
    // handlers and HTML validation. Audit that submit once, without replaying click.
    if (target.form && target.type === 'submit') return;
    const targetId = target.id;
    const href = checkoutLink(target) ? target.href : null;
    const formId = target.form && target.form.id;
    intercept(event, () => {
      if (href) { window.location.assign(href); return; }
      const current = target.isConnected ? target : (targetId && document.getElementById(targetId)) ||
        (formId && document.getElementById(formId)?.querySelector('[name="checkout"]'));
      if (!current || current.disabled) throw new Error('Checkout control unavailable');
      current.click();
    });
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const actionUrl = new URL(form.action, window.location.origin);
    const isCheckout = submitter?.getAttribute('name') === 'checkout' ||
      (actionUrl.origin === window.location.origin && /(^|\/)checkout(?:\/|$)/.test(actionUrl.pathname));
    if (!isCheckout) return;
    const formId = form.id;
    const submitterId = submitter && submitter.id;
    intercept(event, () => {
      const currentForm = form.isConnected ? form : document.getElementById(formId);
      const currentButton = submitter?.isConnected ? submitter :
        (submitterId && document.getElementById(submitterId)) || currentForm?.querySelector('[name="checkout"]');
      if (!currentForm || typeof currentForm.requestSubmit !== 'function') throw new Error('Checkout form unavailable');
      if (currentButton && currentButton.form === currentForm && !currentButton.disabled) currentForm.requestSubmit(currentButton);
      else if (!submitter) currentForm.requestSubmit();
      else throw new Error('Checkout submitter unavailable');
    });
  }, true);

  function subscribeToTheme() {
    // Shrine exposes these as global lexical bindings, not necessarily window properties.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscription = subscribe(PUB_SUB_EVENTS.cartUpdate, auditAfterChange);
    } else if (++subscriptionAttempts < 20) window.setTimeout(subscribeToTheme, 250);
  }
  document.addEventListener('cartQuantityUpdated', auditAfterChange);
  document.addEventListener('vireva:fi-offer:cart-changed', auditAfterChange);
  window.addEventListener('pageshow', auditAfterChange);
  window.VirevaFiOfferCartGuard = Object.freeze({ version: 1, audit, markOfferPending });
  subscribeToTheme();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auditAfterChange, { once: true });
  else auditAfterChange();
})();
